import React, { useEffect, useState, useRef, useCallback } from 'react';
import Layout from '../../components/layout/Layout';
import { getData, postData, putData, deleteData } from '../../services/apiService';
import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography,
    IconButton, Button, Modal, TextField, Select, MenuItem, Pagination, InputAdornment, InputLabel, FormControl
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../../contexts/AuthContext';
import useDebounce from '../../hooks/useDebounce';
import Progress from '../../hooks/useProgressBar';

const Voucher = () => {
    const [loading, setLoading] = useState(true);
    const { user } = useAuth();
    const [data, setData] = useState([]);
    const isFetched = useRef(false);
    const [formData, setFormData] = useState({ code: '', course_id: '', total_uses: '', expiry_date: '', college_id: '', notes: '', amount: '', amount_status: '' });
    const [errors, setErrors] = useState({});
    const [showVoucherModal, addVoucherShowModal] = useState(false);
    const [updateVoucherModalShow, updateVoucherModal] = useState(false);
    const [selectedVoucher, setVoucherStudent] = useState(null);
    const [courseData, setCourse] = useState([]);
    const [collegeData, setcollege] = useState([]);
    const ErrorMessage = ({ error }) => (
        error ? <div className="text-danger small mt-1">{error}</div> : null
    );

    // Pagination and search states
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [totalPages, setTotalPages] = useState(1);
    const debouncedSearchQuery = useDebounce(searchQuery, 500);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getData(`/get-all-vouchers?page=${page}&limit=${rowsPerPage}&search=${debouncedSearchQuery}`);
            if (response) {
                setData(response.data);
                setTotalPages(response.totalPages);
            }
            setLoading(false);
        } catch (error) {
            console.error('Failed to fetch data', error);
        }
        setLoading(false);
    }, [page, rowsPerPage, debouncedSearchQuery]);

    const getCourse = useCallback(async () => {
        try {
            const response = await getData(`/get-courses-for-dropdown`);
            if (response) {
                setCourse(response);
            }
        } catch (error) {
            console.error('Failed to fetch data', error);
        }
    }, []);

    const getCollege = useCallback(async () => {
        try {
            const response = await getData(`/get-colleges-for-dropdown`);
            if (response) {
                setcollege(response);
            }
        } catch (error) {
            console.error('Failed to fetch data', error);
        }
    }, []);

    useEffect(() => {
        if (!isFetched.current) {
            fetchData();
            getCourse();
            getCollege();
        }
    }, [fetchData, getCourse, getCollege]);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setPage(1); // Reset to first page on new search
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(event.target.value);
        setPage(1);
    };
    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value, created_by: user?._id });
    };
    // Add new Voucher!
    const addVoucher = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await postData('/create-voucher', formData);
            setFormData({
                code: '', course_id: '', total_uses: '', expiry_date: '', college_id: '', notes: '', amount: '', amount_status: ''
            });
            setErrors([]);
            addVoucherShowModal(false);
            fetchData();
            setLoading(false);
        } catch (error) {
            if (error.response?.data?.errors) {
                // Convert array of errors to object with field names as keys
                const errorsObj = {};
                error.response.data.errors.forEach(err => {
                    errorsObj[err.path] = err.msg;
                });
                setErrors(errorsObj);
                setTimeout(() => {
                    setErrors({});
                }, 3000);
            }
            setLoading(true);
        }
    };

    // Edit Voucher
    const editVoucher = (id) => {
        const VoucherData = data.find(voucher => voucher._id === id);
        setVoucherStudent(VoucherData);
        updateVoucherModal(true);
    };

    // Update Voucher
    const updateVoucher = async () => {
        if (!selectedVoucher) return;
        const updatedData = {
            code: selectedVoucher.code,
            total_uses: selectedVoucher.total_uses,
            expiry_date: selectedVoucher.expiry_date,
            notes: selectedVoucher.notes,
            course_id: selectedVoucher?.course_id?._id || selectedVoucher.course_id,
            college_id: selectedVoucher?.college_id?._id || selectedVoucher.college_id,
            amount: selectedVoucher?.amount || "",
            amount_status: selectedVoucher?.amount_status || "",
        };

        try {
            await putData(`/update-voucher/${selectedVoucher._id}`, updatedData);
            fetchData();
            updateVoucherModal(false);
            setVoucherStudent(null); // Reset selected student after update
        } catch (error) {
            if (error.response?.data?.errors) {
                const errorsObj = {};
                error.response.data.errors.forEach(err => {
                    errorsObj[err.path] = err.msg;
                });
                setErrors(errorsObj);
            }
            console.error('Failed to update student', error);
        }
    };

    // Delete Voucher!
    const deleteVoucher = async (id) => {
        try {
            await deleteData(`/delete-voucher/${id}`);
            await fetchData();
        } catch (error) {
            console.error("Error:", error);
        }
    };

    // For Add Voucher Modal
    const handleAddVoucherModalClose = (event, reason) => {
        if (reason && reason === "backdropClick") return;
        addVoucherShowModal(false);
    };

    // For Update Voucher Modal
    const handleUpdateVoucherModalClose = (event, reason) => {
        if (reason && reason === "backdropClick") return;
        updateVoucherModal(false);
    };

    // ADD THESE FUNCTIONS INSIDE COMPONENT

    const handleCourseChange = (e) => {
        const selectedCourseId = e.target.value;

        const selectedCourse = courseData.find(
            (course) => course._id === selectedCourseId
        );

        let calculatedAmount = 0;

        if (selectedCourse) {
            calculatedAmount =
                (selectedCourse.amount || 0) * (formData.total_uses || 1);
        }

        setFormData({
            ...formData,
            course_id: selectedCourseId,
            amount: calculatedAmount,
        });
    };

    const handleTotalUsesChange = (e) => {
        const totalUses = e.target.value;

        const selectedCourse = courseData.find(
            (course) => course._id === formData.course_id
        );

        let calculatedAmount = 0;

        if (selectedCourse) {
            calculatedAmount =
                (selectedCourse.amount || 0) * (totalUses || 1);
        }

        setFormData({
            ...formData,
            total_uses: totalUses,
            amount: calculatedAmount,
        });
    };

    const handleUpdateCourseChange = (e) => {
        const selectedCourseId = e.target.value;

        const selectedCourse = courseData.find(
            (course) => course._id === selectedCourseId
        );

        let calculatedAmount = 0;

        if (selectedCourse) {
            calculatedAmount =
                (selectedCourse.amount || 0) *
                (selectedVoucher.total_uses || 1);
        }

        setVoucherStudent({
            ...selectedVoucher,
            course_id: selectedCourseId,
            amount: calculatedAmount,
        });
    };

    const handleUpdateTotalUsesChange = (e) => {
        const totalUses = e.target.value;

        const selectedCourse = courseData.find(
            (course) =>
                course._id ===
                (selectedVoucher?.course_id?._id || selectedVoucher?.course_id)
        );

        let calculatedAmount = 0;

        if (selectedCourse) {
            calculatedAmount =
                (selectedCourse.amount || 0) * (totalUses || 1);
        }

        setVoucherStudent({
            ...selectedVoucher,
            total_uses: totalUses,
            amount: calculatedAmount,
        });
    };

    return (
        <Layout>
            {loading ? <Progress /> :
                <Box className="p-6 bg-gray-100 min-h-screen">
                    <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', mb: 3 }}>Voucher Management</Typography>

                    {/* Header: Add Button (left) and Search (right) */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2, }} >
                        <Button variant="contained" sx={{
                            borderRadius: 2, fontWeight: 600, boxShadow: 2, px: 3, py: 1, textTransform: 'none', backgroundColor: '#4B49AC',
                            '&:hover': { backgroundColor: '#3A3897', },
                        }} onClick={() => addVoucherShowModal(true)} > + Add Voucher </Button>
                        <TextField placeholder="Search Departments" value={searchQuery} onChange={handleSearchChange} variant="outlined" size="medium"
                            sx={{ width: 350, bgcolor: '#e9ecef', borderRadius: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, }, }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#757575' }} />
                                    </InputAdornment>
                                ),
                                sx: { bgcolor: '#e9ecef', borderRadius: 2 },
                            }}
                        />
                    </Box>
                    {/* Table */}
                    {/* <TableContainer component={Paper} elevation={0} sx={{ mb: 2, borderRadius: 2, boxShadow: 2, overflow: 'hidden', }} > */}

                    <TableContainer component={Paper}
                        sx={{
                            width: "100%",
                            maxWidth: "100%",
                            overflowX: "auto",
                            overflowY: "hidden",
                            "&::-webkit-scrollbar": {
                                height: "8px"
                            },
                            "&::-webkit-scrollbar-thumb": {
                                backgroundColor: "#888",
                                borderRadius: "4px"
                            }
                        }}
                    >
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#4B49AC' }}>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600, borderTopLeftRadius: 8 }}> ID </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Code </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Total Uses </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Used </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Expiry Date </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Course </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> College </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Amount </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Amount Status </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Created By </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600, borderTopRightRadius: 8 }}> Action </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.map((item, index) => (
                                    <TableRow key={item._id}>
                                        <TableCell align="center">
                                            <Typography variant="body1">{(page - 1) * rowsPerPage + index + 1}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.code}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.total_uses}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.used}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1"> {new Date(item.expiry_date).toLocaleString()}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item?.course_id?.courseName}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item?.college_id?.collegeName}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item?.amount}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item?.amount_status}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item?.created_by?.firstName}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton color="primary" onClick={() => editVoucher(item._id)}>
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton color="error" onClick={() => deleteVoucher(item._id)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <Pagination count={totalPages} page={page} onChange={handlePageChange} shape="rounded"
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    '&.Mui-selected': {
                                        bgcolor: '#4B49AC',
                                        color: 'white',
                                    },
                                },
                            }}
                        />
                        <Select value={rowsPerPage} onChange={handleRowsPerPageChange} size="small"
                            sx={{ ml: 2, height: 35, bgcolor: '#f7f7f7', border: '1px solid #e2e2e2', '& .MuiSelect-select': { py: 0.75 }, }}
                            IconComponent={KeyboardArrowDownIcon} >
                            <MenuItem value={10}>10 / page</MenuItem>
                            <MenuItem value={20}>20 / page</MenuItem>
                            <MenuItem value={50}>50 / page</MenuItem>
                        </Select>
                    </Box>
                </Box>
            }

            {/* Add Voucher */}
            <Modal open={showVoucherModal} onClose={handleAddVoucherModalClose} aria-labelledby="add-college-modal">
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 900, bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2, }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" component="h2">
                            Add Voucher
                        </Typography>
                        <IconButton onClick={() => addVoucherShowModal(false)}
                            sx={{ position: 'absolute', right: 8, top: 8, color: 'gray' }} >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <form onSubmit={addVoucher}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                <TextField fullWidth label={<>Code <span style={{ color: 'red' }}>*</span> </>} name="code" value={formData.code} onChange={handleInputChange}
                                    margin="normal" sx={{ flex: 1 }} />
                                <ErrorMessage error={errors.code} />
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                {/* <TextField fullWidth label={<>Total Uses <span style={{ color: 'red' }}>*</span> </>} name="total_uses" type="number" value={formData.total_uses} onChange={handleInputChange}
                                    margin="normal" sx={{ flex: 1 }} /> */}

                                <TextField fullWidth label="Total Uses" name="total_uses" type="number" value={formData.total_uses} onChange={handleTotalUsesChange} margin="normal" />
                                <ErrorMessage error={errors.total_uses} />
                            </Box>

                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                <TextField fullWidth label={<>Expiry Date <span style={{ color: 'red' }}>*</span> </>} name="expiry_date" type="date"
                                    InputLabelProps={{ shrink: true, }} value={formData.expiry_date} onChange={handleInputChange}
                                    margin="normal" sx={{ flex: 1 }} />
                                <ErrorMessage error={errors.expiry_date} />
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>College <span style={{ color: 'red' }}>*</span></InputLabel>
                                    <Select name="college_id" value={formData.college_id} onChange={handleInputChange} label="College" >
                                        {collegeData.map((college) => (
                                            <MenuItem key={college._id} value={college._id}>
                                                {college?.collegeName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <ErrorMessage error={errors.college_id} />
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                {/* <FormControl fullWidth margin="normal">
                                    <InputLabel>Course <span style={{ color: 'red' }}>*</span></InputLabel>
                                    <Select name="course_id" value={formData.course_id} onChange={handleInputChange} label="College" >
                                        {courseData.map((course) => (
                                            <MenuItem key={course._id} value={course._id}>
                                                {course?.courseName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl> */}

                                <FormControl fullWidth margin="normal">
                                    <InputLabel>Course *</InputLabel>
                                    <Select
                                        name="course_id"
                                        value={formData.course_id}
                                        onChange={handleCourseChange}
                                        label="Course"
                                    >
                                        {courseData.map((course) => (
                                            <MenuItem key={course._id} value={course._id}>
                                                {course.courseName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <ErrorMessage error={errors.course_id} />
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                <TextField fullWidth label={<>Notes</>} name="notes" value={formData.notes} onChange={handleInputChange}
                                    margin="normal" sx={{ flex: 1 }} multiline minRows={1} maxRows={6}
                                />
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                {/* <TextField fullWidth label={<>Amount <span style={{ color: 'red' }}>*</span> </>} name="amount" type="number" value={formData.amount} onChange={handleInputChange}
                                    margin="normal" sx={{ flex: 1 }} /> */}
                                <TextField
                                    fullWidth
                                    label="Amount"
                                    name="amount"
                                    type="number"
                                    value={formData.amount}
                                    margin="normal"
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                />
                                <ErrorMessage error={errors.amount} />
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>Amount Status *</InputLabel>
                                    <Select
                                        name="amount_status"
                                        value={formData.amount_status}
                                        onChange={handleInputChange}
                                        label="Amount Status"
                                    >
                                        <MenuItem value="Paid">Paid</MenuItem>
                                        <MenuItem value="Unpaid">Unpaid</MenuItem>
                                    </Select>
                                </FormControl>
                                <ErrorMessage error={errors.amount_status} />
                            </Box>
                        </Box>
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button variant="contained" sx={{
                                borderRadius: 2, fontWeight: 600, boxShadow: 2, px: 3, py: 1, textTransform: 'none', backgroundColor: '#4B49AC',
                                '&:hover': { backgroundColor: '#3A3897', },
                            }} type="submit"> Save
                            </Button>
                        </Box>
                    </form>
                </Box>
            </Modal>

            {/* Voucher update model */}
            <Modal open={updateVoucherModalShow} onClose={handleUpdateVoucherModalClose} aria-labelledby="update-college-modal" >
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 900,
                    bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2,
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" component="h2">
                            Update Voucher
                        </Typography>
                        <IconButton onClick={() => updateVoucherModal(false)} sx={{ position: 'absolute', right: 8, top: 8, color: 'gray' }} >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <form>
                        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                <TextField fullWidth label="Code" name="code" value={selectedVoucher?.code || ""}
                                    onChange={(e) => setVoucherStudent({ ...selectedVoucher, code: e.target.value })}
                                    margin="normal" sx={{ flex: 1 }} />
                                <ErrorMessage error={errors.code} />
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                {/* <TextField fullWidth label="Total Uses" name="total_uses" value={selectedVoucher?.total_uses || ""}
                                    onChange={(e) => setVoucherStudent({ ...selectedVoucher, total_uses: e.target.value })}
                                    margin="normal" sx={{ flex: 1 }} /> */}

                                <TextField
                                    fullWidth
                                    label="Total Uses"
                                    value={selectedVoucher?.total_uses || ""}
                                    onChange={handleUpdateTotalUsesChange}
                                    margin="normal"
                                />
                                <ErrorMessage error={errors.total_uses} />
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                <TextField fullWidth label={<>Expiry Date <span style={{ color: 'red' }}>*</span> </>} name="expiry_date" type="date"
                                    InputLabelProps={{ shrink: true, }}
                                    value={selectedVoucher?.expiry_date ? new Date(selectedVoucher.expiry_date).toISOString().split('T')[0] : ""}
                                    onChange={(e) => setVoucherStudent({ ...selectedVoucher, expiry_date: e.target.value })}
                                    margin="normal" sx={{ flex: 1 }} />
                                <ErrorMessage error={errors.expiry_date} />
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>College</InputLabel>
                                    <Select name="college_id" value={selectedVoucher?.college_id?._id || selectedVoucher?.college_id || ""}
                                        onChange={(e) => { setVoucherStudent({ ...selectedVoucher, college_id: e.target.value }); }} label="College" >
                                        {collegeData.map((college) => (
                                            <MenuItem key={college._id} value={college._id}>
                                                {college?.collegeName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>Course</InputLabel>
                                    {/* <Select name="course_id" value={selectedVoucher?.course_id?._id || selectedVoucher?.course_id || ""}
                                        onChange={(e) => { setVoucherStudent({ ...selectedVoucher, course_id: e.target.value }); }} label="College" >
                                        {courseData.map((course) => (
                                            <MenuItem key={course._id} value={course._id}>
                                                {course?.courseName}
                                            </MenuItem>
                                        ))}
                                    </Select> */}
                                    <Select name="course_id" value={selectedVoucher?.course_id?._id || selectedVoucher?.course_id || ""} onChange={handleUpdateCourseChange}
                                        label="College" >
                                        {courseData.map((course) => (
                                            <MenuItem key={course._id} value={course._id}>
                                                {course?.courseName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                <TextField fullWidth label={<>Notes</>} name="notes" value={selectedVoucher?.notes || ""}
                                    onChange={(e) => setVoucherStudent({ ...selectedVoucher, notes: e.target.value })}
                                    margin="normal" sx={{ flex: 1 }} multiline minRows={1} maxRows={6}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                {/* <TextField fullWidth label={<>Amount <span style={{ color: 'red' }}>*</span> </>} name="amount" type="number" value={selectedVoucher?.amount || ""}
                                onChange={(e) => setVoucherStudent({ ...selectedVoucher, amount: e.target.value })}
                                margin="normal" sx={{ flex: 1 }} /> */}
                                <TextField
                                    fullWidth
                                    label="Amount"
                                    value={selectedVoucher?.amount || ""}
                                    margin="normal"
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                />
                                <ErrorMessage error={errors.amount} />
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>Amount Status</InputLabel>
                                    <Select
                                        value={selectedVoucher?.amount_status || ""}
                                        onChange={(e) =>
                                            setVoucherStudent({
                                                ...selectedVoucher,
                                                amount_status: e.target.value,
                                            })
                                        }
                                        label="Amount Status"
                                    >
                                        <MenuItem value="Paid">Paid</MenuItem>
                                        <MenuItem value="Unpaid">Unpaid</MenuItem>
                                    </Select>
                                </FormControl>
                                <ErrorMessage error={errors.amount_status} />
                            </Box>
                        </Box>
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button variant="contained" sx={{
                                borderRadius: 2, fontWeight: 600, boxShadow: 2, px: 3, py: 1, textTransform: 'none', backgroundColor: '#4B49AC', '&:hover': { backgroundColor: '#3A3897', },
                            }} onClick={updateVoucher}>
                                Save changes
                            </Button>
                        </Box>
                    </form>
                </Box>
            </Modal>
        </Layout>
    );
};

export default Voucher;